import type {
  DomainCanvas,
  DomainDocument,
  EntityId,
  KnowledgeItem,
  PageResult,
  SaveReceipt,
  UnixTimestamp,
} from "@note-app/contracts";
import {
  CURRENT_CONTENT_SCHEMA_VERSION,
  classifyContentSchemaVersion,
} from "@note-app/contracts";
import {
  assertCanMoveToTrash,
  assertCanRestore,
  assertCanvas,
  assertDocument,
  assertFolder,
  assertKnowledgeItem,
  assertNewEntity,
  assertRevisionRecord,
  assertTag,
  assertTimestampTransition,
  createFolder,
  createTag,
  migrateCanvasToCurrent,
  migrateDocumentToCurrent,
  migrateKnowledgeItemToCurrent,
  nextRevision,
  type Folder,
  type IdGenerator,
  type RevisionRecord,
  type Tag,
} from "@note-app/domain";
import type {
  AttachmentRecord,
  AttachmentRepository,
} from "@note-app/knowledge-services";
import {
  ConstraintViolationError,
  DEFAULT_HISTORY_RETENTION_POLICY,
  DEFAULT_TRASH_RETENTION_POLICY,
  EntityAlreadyExistsError,
  EntityNotFoundError,
  InvalidCursorError,
  OptimisticConcurrencyError,
  ReadOnlyStorageError,
  validateTrashRetentionPolicy,
  type CanvasRepository,
  type ContentLoadOptions,
  type ContentSaveOptions,
  type DocumentRepository,
  type EntityListOptions,
  type FolderRepository,
  type FolderUpdatePatch,
  type HistoryRetentionPolicy,
  type ItemGetOptions,
  type ItemListOptions,
  type ItemRepository,
  type ItemUpdatePatch,
  type PurgeResult,
  type Repositories,
  type RevisionListOptions,
  type RevisionRepository,
  type StoredCanvas,
  type StoredDocument,
  type TagRepository,
  type TagUpdatePatch,
  type TrashRepository,
  type TrashRetentionPolicy,
  type UnitOfWork,
} from "@note-app/storage";
import {
  openDB,
  type IDBPDatabase,
  type IDBPTransaction,
} from "idb";

import {
  IndexedDbStorageError,
  mapIndexedDbError,
} from "./errors.ts";
import {
  ALL_KNOWLEDGE_STORES,
  INDEXED_DB_SCHEMA_VERSION,
  type ChangeRecord,
  type KnowledgeDbSchema,
  type KnowledgeStoreName,
} from "./schema.ts";

type WriteTransaction = IDBPTransaction<
  KnowledgeDbSchema,
  KnowledgeStoreName[],
  "readwrite"
>;

interface RepositoryContext {
  readonly db: IDBPDatabase<KnowledgeDbSchema>;
  readonly ids: IdGenerator;
  readonly history: HistoryRetentionPolicy;
  readonly readOnly: boolean;
  readonly transaction?: WriteTransaction;
}

export interface IndexedDbKnowledgeStoreOptions {
  readonly name?: string;
  readonly ids?: IdGenerator;
  readonly historyRetention?: HistoryRetentionPolicy;
  readonly onBlocked?: () => void;
  readonly onVersionChange?: () => void;
}

export interface ChangeFeedPage {
  readonly changes: readonly Required<ChangeRecord>[];
  readonly cursor?: number;
}

export interface ChangeFeedPort {
  list(afterSequence?: number, limit?: number): Promise<ChangeFeedPage>;
}

export class IndexedDbKnowledgeStore implements UnitOfWork {
  public readonly repositories: Repositories;
  public readonly attachments: AttachmentRepository;
  public readonly changes: ChangeFeedPort;

  public constructor(
    private readonly context: RepositoryContext,
    public readonly name: string,
  ) {
    this.repositories = createRepositories(context);
    this.attachments = new IndexedDbAttachmentRepository(context);
    this.changes = new IndexedDbChangeFeed(context);
  }

  public get readOnly(): boolean {
    return this.context.readOnly;
  }

  public get schemaVersion(): number {
    return this.context.db.version;
  }

  public async knowledgeBaseId(): Promise<string> {
    const row = await this.context.db.get("meta", "knowledgeBaseId");
    if (typeof row?.value !== "string") {
      if (this.readOnly) {
        throw new IndexedDbStorageError(
          "corrupt-data",
          "The read-only knowledge base has no valid identity.",
        );
      }
      const value = this.context.ids.next();
      await this.context.db.put("meta", { key: "knowledgeBaseId", value });
      return value;
    }
    return row.value;
  }

  public async transaction<T>(
    work: (repositories: Repositories) => Promise<T>,
  ): Promise<T> {
    this.assertWritable();
    const transaction = this.context.db.transaction(
      [...ALL_KNOWLEDGE_STORES],
      "readwrite",
      { durability: "strict" },
    );
    const scoped = createRepositories({
      ...this.context,
      transaction,
    });
    try {
      const result = await work(scoped);
      await transaction.done;
      return result;
    } catch (error) {
      try {
        transaction.abort();
      } catch {
        // The browser may already have aborted the transaction.
      }
      try {
        await transaction.done;
      } catch {
        // Preserve the original domain/storage failure.
      }
      throw mapWriteError(error);
    }
  }

  public async reset(): Promise<void> {
    this.assertWritable();
    const transaction = this.context.db.transaction(
      [...ALL_KNOWLEDGE_STORES],
      "readwrite",
      { durability: "strict" },
    );
    await Promise.all(
      ALL_KNOWLEDGE_STORES
        .filter((name) => name !== "meta")
        .map((name) => transaction.objectStore(name).clear()),
    );
    await transaction.done;
  }

  public close(): void {
    this.context.db.close();
  }

  public assertWritable(): void {
    if (this.readOnly) {
      throw new ReadOnlyStorageError(
        "This knowledge base was created by a newer FengYue Notes version and is open read-only.",
      );
    }
  }

  /** Test/recovery seam for validated notebundle restores. */
  public database(): IDBPDatabase<KnowledgeDbSchema> {
    return this.context.db;
  }
}

export async function openIndexedDbKnowledgeStore(
  options: IndexedDbKnowledgeStoreOptions = {},
): Promise<IndexedDbKnowledgeStore> {
  const name = options.name ?? "fengyue-notes";
  const ids = options.ids ?? {
    next: () => crypto.randomUUID(),
  };
  let readOnly = false;
  let database: IDBPDatabase<KnowledgeDbSchema>;
  try {
    database = await openDB<KnowledgeDbSchema>(
      name,
      INDEXED_DB_SCHEMA_VERSION,
      {
        upgrade(db, oldVersion, _newVersion, transaction) {
          migrateDatabase(db, oldVersion, transaction);
        },
        blocking() {
          options.onVersionChange?.();
        },
        ...(options.onBlocked === undefined
          ? {}
          : { blocked: options.onBlocked }),
      },
    );
  } catch (error) {
    if (isVersionError(error)) {
      database = await openDB<KnowledgeDbSchema>(name, undefined, {
        blocking() {
          options.onVersionChange?.();
        },
        ...(options.onBlocked === undefined
          ? {}
          : { blocked: options.onBlocked }),
      });
      readOnly = true;
    } else {
      throw mapIndexedDbError(
        error,
        "IndexedDB could not be opened. Private browsing or browser policy may be blocking durable storage.",
      );
    }
  }

  if (!readOnly) {
    readOnly = await containsFutureContent(database);
  }
  const context: RepositoryContext = {
    db: database,
    ids,
    history:
      options.historyRetention ?? DEFAULT_HISTORY_RETENTION_POLICY,
    readOnly,
  };
  const store = new IndexedDbKnowledgeStore(context, name);
  if (!readOnly) await store.knowledgeBaseId();
  return store;
}

function migrateDatabase(
  db: IDBPDatabase<KnowledgeDbSchema>,
  oldVersion: number,
  transaction: IDBPTransaction<
    KnowledgeDbSchema,
    KnowledgeStoreName[],
    "versionchange"
  >,
): void {
  if (oldVersion < 1) {
    db.createObjectStore("meta", { keyPath: "key" });
    const items = db.createObjectStore("items", { keyPath: "id" });
    items.createIndex("by-updated", ["updatedAt", "id"]);
    items.createIndex("by-kind", "kind");
    items.createIndex("by-folder", "folderId");
    db.createObjectStore("documents", { keyPath: "itemId" });
    db.createObjectStore("canvases", { keyPath: "itemId" });
    const folders = db.createObjectStore("folders", { keyPath: "id" });
    folders.createIndex("by-name", ["name", "id"]);
    const tags = db.createObjectStore("tags", { keyPath: "id" });
    tags.createIndex("by-name", ["name", "id"]);
  }
  if (oldVersion < 2) {
    const revisions = db.createObjectStore("revisions", { keyPath: "id" });
    revisions.createIndex("by-item", "itemId");
    revisions.createIndex("by-item-revision", ["itemId", "revision"]);
    const changes = db.createObjectStore("changes", {
      keyPath: "sequence",
      autoIncrement: true,
    });
    changes.createIndex("by-entity", "entityId");
    changes.createIndex("by-committed", "committedAt");
  }
  if (oldVersion < 3) {
    const attachments = db.createObjectStore("attachments", { keyPath: "id" });
    attachments.createIndex("by-sha256", "sha256", { unique: true });
    db.createObjectStore("blobs", { keyPath: "storageKey" });
    transaction.objectStore("meta").put({
      key: "migration",
      value: {
        version: INDEXED_DB_SCHEMA_VERSION,
        migratedAt: Date.now(),
      },
    });
  }
}

async function containsFutureContent(
  db: IDBPDatabase<KnowledgeDbSchema>,
): Promise<boolean> {
  for (const storeName of [
    "items",
    "folders",
    "tags",
    "attachments",
  ] as const) {
    const values = await db.getAll(storeName);
    if (values.some(
      (value) =>
        "schemaVersion" in value &&
        typeof value.schemaVersion === "number" &&
        classifyContentSchemaVersion(value.schemaVersion) === "future",
    )) {
      return true;
    }
  }
  if ((await db.getAll("documents")).some(
    (row) =>
      classifyContentSchemaVersion(row.document.schemaVersion) === "future",
  )) {
    return true;
  }
  if ((await db.getAll("canvases")).some(
    (row) =>
      classifyContentSchemaVersion(row.canvas.schemaVersion) === "future",
  )) {
    return true;
  }
  return false;
}

function createRepositories(context: RepositoryContext): Repositories {
  return {
    items: new IndexedDbItemRepository(context),
    documents: new IndexedDbDocumentRepository(context),
    canvases: new IndexedDbCanvasRepository(context),
    folders: new IndexedDbFolderRepository(context),
    tags: new IndexedDbTagRepository(context),
    revisions: new IndexedDbRevisionRepository(context),
    trash: new IndexedDbTrashRepository(context),
  };
}

class IndexedDbItemRepository implements ItemRepository {
  public constructor(private readonly context: RepositoryContext) {}

  async create(item: KnowledgeItem): Promise<void> {
    assertWrite(this.context);
    assertKnowledgeItem(item);
    assertNewEntity(item);
    await runWrite(this.context, ["items", "folders", "tags", "changes"], async (tx) => {
      if (await tx.objectStore("items").get(item.id)) {
        throw new EntityAlreadyExistsError("item", item.id);
      }
      if (
        item.folderId !== undefined &&
        !(await tx.objectStore("folders").get(item.folderId))
      ) {
        throw new ConstraintViolationError("Item references a missing folder");
      }
      for (const tagId of item.tags) {
        if (!(await tx.objectStore("tags").get(tagId))) {
          throw new ConstraintViolationError("Item references a missing tag");
        }
      }
      await tx.objectStore("items").add(clone(item));
      await appendChange(tx, {
        entityId: item.id,
        entityType: "item",
        operation: "create",
        revision: item.revision,
        committedAt: item.createdAt,
        payload: { kind: item.kind, title: item.title },
      });
    });
  }

  async get(
    id: EntityId,
    options: ItemGetOptions = {},
  ): Promise<KnowledgeItem | null> {
    const value = await (
      this.context.transaction?.objectStore("items").get(id) ??
      this.context.db.get("items", id)
    );
    if (!value || (value.deletedAt !== undefined && !options.includeDeleted)) {
      return null;
    }
    return checkedItem(value);
  }

  async update(
    id: EntityId,
    patch: ItemUpdatePatch,
    expectedRevision: number,
    updatedAt: UnixTimestamp,
  ): Promise<KnowledgeItem> {
    return this.mutate(id, expectedRevision, async (current, tx) => {
      assertTimestampTransition(current.updatedAt, updatedAt);
      const folderId =
        "folderId" in patch ? patch.folderId ?? undefined : current.folderId;
      if (
        folderId !== undefined &&
        !(await tx.objectStore("folders").get(folderId))
      ) {
        throw new ConstraintViolationError("Item references a missing folder");
      }
      const tags = patch.tags === undefined ? current.tags : [...patch.tags];
      for (const tagId of tags) {
        if (!(await tx.objectStore("tags").get(tagId))) {
          throw new ConstraintViolationError("Item references a missing tag");
        }
      }
      const icon = "icon" in patch ? patch.icon ?? undefined : current.icon;
      const {
        icon: _oldIcon,
        folderId: _oldFolder,
        ...required
      } = current;
      const value: KnowledgeItem = {
        ...required,
        title: patch.title ?? current.title,
        tags,
        properties:
          patch.properties === undefined
            ? current.properties
            : { ...patch.properties },
        revision: nextRevision(current.revision),
        updatedAt,
        ...(icon === undefined ? {} : { icon }),
        ...(folderId === undefined ? {} : { folderId }),
      };
      assertKnowledgeItem(value);
      return {
        value,
        operation: "update" as const,
        payload: { fields: Object.keys(patch) },
      };
    });
  }

  async moveToTrash(
    id: EntityId,
    expectedRevision: number,
    deletedAt: UnixTimestamp,
  ): Promise<KnowledgeItem> {
    return this.mutate(id, expectedRevision, async (current) => {
      assertCanMoveToTrash(current);
      assertTimestampTransition(current.updatedAt, deletedAt, "deletedAt");
      return {
        value: {
          ...current,
          revision: nextRevision(current.revision),
          updatedAt: deletedAt,
          deletedAt,
        },
        operation: "trash" as const,
        payload: {},
      };
    }, true);
  }

  async restore(
    id: EntityId,
    expectedRevision: number,
    restoredAt: UnixTimestamp,
    fallbackFolderId?: EntityId | null,
  ): Promise<KnowledgeItem> {
    return this.mutate(id, expectedRevision, async (current) => {
      assertCanRestore(current);
      assertTimestampTransition(current.updatedAt, restoredAt, "restoredAt");
      const {
        deletedAt: _deleted,
        folderId: _oldFolder,
        ...required
      } = current;
      const folderId =
        fallbackFolderId === undefined
          ? current.folderId
          : fallbackFolderId ?? undefined;
      return {
        value: {
          ...required,
          revision: nextRevision(current.revision),
          updatedAt: restoredAt,
          ...(folderId === undefined ? {} : { folderId }),
        },
        operation: "restore" as const,
        payload: { rehomed: fallbackFolderId === null },
      };
    }, true);
  }

  async list(options: ItemListOptions = {}): Promise<PageResult<KnowledgeItem>> {
    const values = await this.context.db.getAll("items");
    const deletion = options.deletion ?? "active";
    const filtered = values
      .map(checkedItem)
      .filter((item) => {
        if (deletion === "active" && item.deletedAt !== undefined) return false;
        if (deletion === "trash" && item.deletedAt === undefined) return false;
        if (options.kind !== undefined && item.kind !== options.kind) return false;
        if ("folderId" in options) {
          const folder = item.folderId ?? null;
          if (folder !== options.folderId) return false;
        }
        return true;
      })
      .sort((left, right) => right.updatedAt - left.updatedAt || left.id.localeCompare(right.id));
    return page(filtered, options, (value) => value.updatedAt);
  }

  private async mutate(
    id: EntityId,
    expectedRevision: number,
    transform: (
      current: KnowledgeItem,
      tx: WriteTransaction,
    ) => Promise<{
      value: KnowledgeItem;
      operation: ChangeRecord["operation"];
      payload: unknown;
    }>,
    includeDeleted = false,
  ): Promise<KnowledgeItem> {
    assertWrite(this.context);
    return runWrite(
      this.context,
      ["items", "folders", "tags", "changes"],
      async (tx) => {
        const storedCurrent = await tx.objectStore("items").get(id);
        if (
          !storedCurrent ||
          (storedCurrent.deletedAt !== undefined && !includeDeleted)
        ) {
          throw new EntityNotFoundError("item", id);
        }
        const current = checkedItem(storedCurrent);
        assertRevision(id, expectedRevision, current.revision);
        const result = await transform(current, tx);
        assertKnowledgeItem(result.value);
        await tx.objectStore("items").put(clone(result.value));
        await appendChange(tx, {
          entityId: id,
          entityType: "item",
          operation: result.operation,
          revision: result.value.revision,
          committedAt: result.value.updatedAt,
          payload: result.payload,
        });
        return clone(result.value);
      },
    );
  }
}

class IndexedDbDocumentRepository implements DocumentRepository {
  public constructor(private readonly context: RepositoryContext) {}

  async load(
    itemId: EntityId,
    options: ContentLoadOptions = {},
  ): Promise<StoredDocument | null> {
    const row = await (
      this.context.transaction?.objectStore("documents").get(itemId) ??
      this.context.db.get("documents", itemId)
    );
    const item = await (
      this.context.transaction?.objectStore("items").get(itemId) ??
      this.context.db.get("items", itemId)
    );
    if (
      !row ||
      !item ||
      (item.deletedAt !== undefined && !options.includeDeleted)
    ) return null;
    try {
      const document = migrateDocumentToCurrent(clone(row.document)).value;
      return {
        itemId,
        revision: checkedItem(item).revision,
        contentRevision: row.contentRevision,
        document,
      };
    } catch (error) {
      throw corrupt("document", itemId, error);
    }
  }

  save(
    itemId: EntityId,
    document: DomainDocument,
    expectedRevision: number,
    committedAt: UnixTimestamp,
    options: ContentSaveOptions = {},
  ): Promise<SaveReceipt> {
    assertDocument(document);
    return saveContent(
      this.context,
      "document",
      itemId,
      document,
      expectedRevision,
      committedAt,
      options,
    );
  }
}

class IndexedDbCanvasRepository implements CanvasRepository {
  public constructor(private readonly context: RepositoryContext) {}

  async load(
    itemId: EntityId,
    options: ContentLoadOptions = {},
  ): Promise<StoredCanvas | null> {
    const row = await (
      this.context.transaction?.objectStore("canvases").get(itemId) ??
      this.context.db.get("canvases", itemId)
    );
    const item = await (
      this.context.transaction?.objectStore("items").get(itemId) ??
      this.context.db.get("items", itemId)
    );
    if (
      !row ||
      !item ||
      (item.deletedAt !== undefined && !options.includeDeleted)
    ) return null;
    try {
      const canvas = migrateCanvasToCurrent(clone(row.canvas)).value;
      return {
        itemId,
        revision: checkedItem(item).revision,
        contentRevision: row.contentRevision,
        canvas,
      };
    } catch (error) {
      throw corrupt("canvas", itemId, error);
    }
  }

  save(
    itemId: EntityId,
    canvas: DomainCanvas,
    expectedRevision: number,
    committedAt: UnixTimestamp,
    options: ContentSaveOptions = {},
  ): Promise<SaveReceipt> {
    assertCanvas(canvas);
    return saveContent(
      this.context,
      "canvas",
      itemId,
      canvas,
      expectedRevision,
      committedAt,
      options,
    );
  }
}

async function saveContent(
  context: RepositoryContext,
  kind: "document" | "canvas",
  itemId: EntityId,
  content: DomainDocument | DomainCanvas,
  expectedRevision: number,
  committedAt: UnixTimestamp,
  options: ContentSaveOptions,
): Promise<SaveReceipt> {
  assertWrite(context);
  return runWrite(
    context,
    [
      "items",
      kind === "document" ? "documents" : "canvases",
      "attachments",
      "revisions",
      "changes",
    ],
    async (tx) => {
      const item = await tx.objectStore("items").get(itemId);
      if (!item || item.deletedAt !== undefined) {
        throw new EntityNotFoundError("active item", itemId);
      }
      if (item.kind !== kind) {
        throw new ConstraintViolationError(
          `Item ${itemId} is ${item.kind}, not ${kind}`,
        );
      }
      assertRevision(itemId, expectedRevision, item.revision);
      assertTimestampTransition(item.updatedAt, committedAt, "committedAt");
      let previousContent: DomainDocument | DomainCanvas | undefined;
      if (kind === "document") {
        previousContent = (
          await tx.objectStore("documents").get(itemId)
        )?.document;
      } else {
        previousContent = (
          await tx.objectStore("canvases").get(itemId)
        )?.canvas;
      }
      await reconcileAttachmentReferences(
        tx,
        previousContent,
        content,
        committedAt,
      );
      const revision = nextRevision(item.revision);
      const updated = { ...item, revision, updatedAt: committedAt };
      await tx.objectStore("items").put(updated);
      if (kind === "document") {
        await tx.objectStore("documents").put({
          itemId,
          revision,
          contentRevision: revision,
          document: clone(content as DomainDocument),
        });
      } else {
        await tx.objectStore("canvases").put({
          itemId,
          revision,
          contentRevision: revision,
          canvas: clone(content as DomainCanvas),
        });
      }
      const revisionRecord: RevisionRecord = {
        id: context.ids.next(),
        itemId,
        entityType: kind,
        operation: options.operation ?? "save",
        revision,
        schemaVersion: content.schemaVersion,
        snapshot: clone(content) as unknown as RevisionRecord["snapshot"],
        createdAt: committedAt,
      };
      assertRevisionRecord(revisionRecord);
      await tx.objectStore("revisions").put(revisionRecord);
      await pruneRevisions(tx, itemId, context.history.revisionsPerItem);
      await appendChange(tx, {
        entityId: itemId,
        entityType: kind,
        operation: "save",
        revision,
        committedAt,
        payload: clone(content),
      }, context.history.changesPerEntity);
      return { entityId: itemId, revision, committedAt };
    },
  );
}

class IndexedDbFolderRepository implements FolderRepository {
  public constructor(private readonly context: RepositoryContext) {}

  async create(folder: Folder): Promise<void> {
    assertWrite(this.context);
    assertFolder(folder);
    assertNewEntity(folder);
    await runWrite(this.context, ["folders", "changes"], async (tx) => {
      if (await tx.objectStore("folders").get(folder.id)) {
        throw new EntityAlreadyExistsError("folder", folder.id);
      }
      await requireFolderParent(tx, folder.id, folder.parentId);
      await tx.objectStore("folders").add(clone(folder));
      await appendChange(tx, changeFor(folder, "folder", "create", { name: folder.name }));
    });
  }

  async get(id: EntityId, includeDeleted = false): Promise<Folder | null> {
    const value = await (
      this.context.transaction?.objectStore("folders").get(id) ??
      this.context.db.get("folders", id)
    );
    if (!value || (value.deletedAt !== undefined && !includeDeleted)) return null;
    return checkedCatalogEntity("folder", value, assertFolder);
  }

  async update(
    id: EntityId,
    patch: FolderUpdatePatch,
    expectedRevision: number,
    updatedAt: UnixTimestamp,
  ): Promise<Folder> {
    return this.mutate(id, expectedRevision, async (current, tx) => {
      assertTimestampTransition(current.updatedAt, updatedAt);
      const parentId =
        "parentId" in patch ? patch.parentId ?? undefined : current.parentId;
      await requireFolderParent(tx, id, parentId);
      const { parentId: _old, ...required } = current;
      const value: Folder = {
        ...required,
        name: patch.name ?? current.name,
        position: patch.position ?? current.position,
        revision: nextRevision(current.revision),
        updatedAt,
        ...(parentId === undefined ? {} : { parentId }),
      };
      return { value, operation: "update" as const };
    });
  }

  async moveToTrash(
    id: EntityId,
    expectedRevision: number,
    deletedAt: UnixTimestamp,
  ): Promise<Folder> {
    return this.mutate(id, expectedRevision, async (current) => {
      assertCanMoveToTrash(current);
      assertTimestampTransition(current.updatedAt, deletedAt, "deletedAt");
      return {
        value: {
          ...current,
          revision: nextRevision(current.revision),
          updatedAt: deletedAt,
          deletedAt,
        },
        operation: "trash" as const,
      };
    }, true);
  }

  async restore(
    id: EntityId,
    expectedRevision: number,
    restoredAt: UnixTimestamp,
  ): Promise<Folder> {
    return this.mutate(id, expectedRevision, async (current, tx) => {
      assertCanRestore(current);
      assertTimestampTransition(current.updatedAt, restoredAt, "restoredAt");
      const parent =
        current.parentId === undefined
          ? undefined
          : await tx.objectStore("folders").get(current.parentId);
      const { deletedAt: _deleted, parentId: _parent, ...required } = current;
      return {
        value: {
          ...required,
          revision: nextRevision(current.revision),
          updatedAt: restoredAt,
          ...(parent && parent.deletedAt === undefined
            ? { parentId: current.parentId }
            : {}),
        },
        operation: "restore" as const,
      };
    }, true);
  }

  async list(options: EntityListOptions = {}): Promise<PageResult<Folder>> {
    const values = (await this.context.db.getAll("folders"))
      .map((value) => checkedCatalogEntity("folder", value, assertFolder))
      .filter(deletionPredicate(options))
      .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
    return page(values, options, (value) => value.name.toLocaleLowerCase());
  }

  private async mutate(
    id: string,
    expectedRevision: number,
    transform: (
      current: Folder,
      tx: WriteTransaction,
    ) => Promise<{ value: Folder; operation: ChangeRecord["operation"] }>,
    includeDeleted = false,
  ): Promise<Folder> {
    assertWrite(this.context);
    return runWrite(this.context, ["folders", "changes"], async (tx) => {
      const storedCurrent = await tx.objectStore("folders").get(id);
      if (
        !storedCurrent ||
        (storedCurrent.deletedAt !== undefined && !includeDeleted)
      ) {
        throw new EntityNotFoundError("folder", id);
      }
      const current = checkedCatalogEntity(
        "folder",
        storedCurrent,
        assertFolder,
      );
      assertRevision(id, expectedRevision, current.revision);
      const result = await transform(current, tx);
      assertFolder(result.value);
      await tx.objectStore("folders").put(result.value);
      await appendChange(tx, changeFor(result.value, "folder", result.operation, {}));
      return clone(result.value);
    });
  }
}

class IndexedDbTagRepository implements TagRepository {
  public constructor(private readonly context: RepositoryContext) {}

  async create(tag: Tag): Promise<void> {
    assertWrite(this.context);
    assertTag(tag);
    assertNewEntity(tag);
    await runWrite(this.context, ["tags", "changes"], async (tx) => {
      if (await tx.objectStore("tags").get(tag.id)) {
        throw new EntityAlreadyExistsError("tag", tag.id);
      }
      await tx.objectStore("tags").add(clone(tag));
      await appendChange(tx, changeFor(tag, "tag", "create", { name: tag.name }));
    });
  }

  async get(id: EntityId, includeDeleted = false): Promise<Tag | null> {
    const value = await (
      this.context.transaction?.objectStore("tags").get(id) ??
      this.context.db.get("tags", id)
    );
    if (!value || (value.deletedAt !== undefined && !includeDeleted)) return null;
    return checkedCatalogEntity("tag", value, assertTag);
  }

  async update(
    id: EntityId,
    patch: TagUpdatePatch,
    expectedRevision: number,
    updatedAt: UnixTimestamp,
  ): Promise<Tag> {
    return this.mutate(id, expectedRevision, (current) => {
      assertTimestampTransition(current.updatedAt, updatedAt);
      const color = "color" in patch ? patch.color ?? undefined : current.color;
      const { color: _old, ...required } = current;
      return {
        ...required,
        name: patch.name ?? current.name,
        revision: nextRevision(current.revision),
        updatedAt,
        ...(color === undefined ? {} : { color }),
      };
    });
  }

  async moveToTrash(
    id: EntityId,
    expectedRevision: number,
    deletedAt: UnixTimestamp,
  ): Promise<Tag> {
    return this.mutate(id, expectedRevision, (current) => {
      assertCanMoveToTrash(current);
      assertTimestampTransition(current.updatedAt, deletedAt, "deletedAt");
      return {
        ...current,
        revision: nextRevision(current.revision),
        updatedAt: deletedAt,
        deletedAt,
      };
    }, true, "trash");
  }

  async restore(
    id: EntityId,
    expectedRevision: number,
    restoredAt: UnixTimestamp,
  ): Promise<Tag> {
    return this.mutate(id, expectedRevision, (current) => {
      assertCanRestore(current);
      assertTimestampTransition(current.updatedAt, restoredAt, "restoredAt");
      const { deletedAt: _deleted, ...required } = current;
      return {
        ...required,
        revision: nextRevision(current.revision),
        updatedAt: restoredAt,
      };
    }, true, "restore");
  }

  async list(options: EntityListOptions = {}): Promise<PageResult<Tag>> {
    const values = (await this.context.db.getAll("tags"))
      .map((value) => checkedCatalogEntity("tag", value, assertTag))
      .filter(deletionPredicate(options))
      .sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
    return page(values, options, (value) => value.name.toLocaleLowerCase());
  }

  private async mutate(
    id: string,
    expectedRevision: number,
    transform: (current: Tag) => Tag,
    includeDeleted = false,
    operation: ChangeRecord["operation"] = "update",
  ): Promise<Tag> {
    assertWrite(this.context);
    return runWrite(this.context, ["tags", "changes"], async (tx) => {
      const storedCurrent = await tx.objectStore("tags").get(id);
      if (
        !storedCurrent ||
        (storedCurrent.deletedAt !== undefined && !includeDeleted)
      ) {
        throw new EntityNotFoundError("tag", id);
      }
      const current = checkedCatalogEntity("tag", storedCurrent, assertTag);
      assertRevision(id, expectedRevision, current.revision);
      const value = transform(current);
      assertTag(value);
      await tx.objectStore("tags").put(value);
      await appendChange(tx, changeFor(value, "tag", operation, {}));
      return clone(value);
    });
  }
}

const TRASH_RETENTION_KEY = "trash.retention";

/**
 * Owns irreversible item lifecycle operations for browser and mobile stores.
 * Content and revisions are removed atomically, while detached attachment
 * metadata/blobs remain available to the regular attachment garbage collector.
 */
class IndexedDbTrashRepository implements TrashRepository {
  public constructor(private readonly context: RepositoryContext) {}

  async getRetentionPolicy(): Promise<TrashRetentionPolicy> {
    const row = await (
      this.context.transaction?.objectStore("meta").get(TRASH_RETENTION_KEY) ??
      this.context.db.get("meta", TRASH_RETENTION_KEY)
    );
    if (row === undefined) return { ...DEFAULT_TRASH_RETENTION_POLICY };
    return parseTrashRetentionPolicy(row.value);
  }

  async setRetentionPolicy(
    policy: TrashRetentionPolicy,
    updatedAt: UnixTimestamp,
  ): Promise<TrashRetentionPolicy> {
    assertWrite(this.context);
    validateTrashRetentionPolicy(policy);
    if (!Number.isSafeInteger(updatedAt) || updatedAt < 0) {
      throw new RangeError("updatedAt must be a non-negative safe integer");
    }
    const stored = { ...policy };
    await runWrite(this.context, ["meta"], async (tx) => {
      await tx.objectStore("meta").put({
        key: TRASH_RETENTION_KEY,
        value: { ...stored, updatedAt },
      });
    });
    return stored;
  }

  async purgeItem(
    itemId: EntityId,
    expectedRevision: number,
  ): Promise<PurgeResult> {
    return runWrite(this.context, purgeStoreNames(), (tx) =>
      this.purgeOne(tx, itemId, expectedRevision));
  }

  async purgeDeletedBefore(
    cutoff: UnixTimestamp,
  ): Promise<readonly PurgeResult[]> {
    return this.purgeMatching(
      (item) => item.deletedAt !== undefined && item.deletedAt <= cutoff,
    );
  }

  async empty(): Promise<readonly PurgeResult[]> {
    return this.purgeMatching((item) => item.deletedAt !== undefined);
  }

  private async purgeMatching(
    predicate: (item: KnowledgeItem) => boolean,
  ): Promise<readonly PurgeResult[]> {
    return runWrite(this.context, purgeStoreNames(), async (tx) => {
      const items = (await tx.objectStore("items").getAll())
        .filter(predicate)
        .sort(
          (left, right) =>
            (left.deletedAt ?? 0) - (right.deletedAt ?? 0) ||
            left.id.localeCompare(right.id),
        );
      const results: PurgeResult[] = [];
      for (const item of items) {
        results.push(await this.purgeOne(tx, item.id, item.revision));
      }
      return results;
    });
  }

  private async purgeOne(
    tx: WriteTransaction,
    itemId: EntityId,
    expectedRevision: number,
  ): Promise<PurgeResult> {
    const item = await tx.objectStore("items").get(itemId);
    if (item === undefined || item.deletedAt === undefined) {
      throw new EntityNotFoundError("trashed item", itemId);
    }
    assertRevision(itemId, expectedRevision, item.revision);

    const [document, canvas, revisions] = await Promise.all([
      tx.objectStore("documents").get(itemId),
      tx.objectStore("canvases").get(itemId),
      tx.objectStore("revisions").index("by-item").getAll(itemId),
    ]);
    const attachmentOccurrences = collectAttachmentOccurrences(
      document?.document ?? canvas?.canvas,
    );

    await Promise.all([
      tx.objectStore("items").delete(itemId),
      tx.objectStore("documents").delete(itemId),
      tx.objectStore("canvases").delete(itemId),
      ...revisions.map((revision) =>
        tx.objectStore("revisions").delete(revision.id)),
    ]);

    for (const [attachmentId, occurrences] of attachmentOccurrences) {
      const attachment = await tx.objectStore("attachments").get(attachmentId);
      if (attachment === undefined || attachment.referenceCount === 0) continue;
      const referenceCount = Math.max(
        0,
        attachment.referenceCount - occurrences,
      );
      const {
        unreferencedAt: _previouslyUnreferencedAt,
        ...required
      } = attachment;
      await tx.objectStore("attachments").put({
        ...required,
        referenceCount,
        ...(referenceCount === 0 ? { unreferencedAt: item.deletedAt } : {}),
      });
    }

    return {
      itemId,
      removedRevisionCount: revisions.length,
      detachedAttachmentIds: [...attachmentOccurrences.keys()].sort(),
    };
  }
}

class IndexedDbRevisionRepository implements RevisionRepository {
  public constructor(private readonly context: RepositoryContext) {}

  async get(id: EntityId): Promise<RevisionRecord | null> {
    const value = await (
      this.context.transaction?.objectStore("revisions").get(id) ??
      this.context.db.get("revisions", id)
    );
    return value ? checkedRevision(value) : null;
  }

  async getByItemRevision(
    itemId: EntityId,
    revision: number,
  ): Promise<RevisionRecord | null> {
    const values = await (
      this.context.transaction
        ?.objectStore("revisions")
        .index("by-item-revision")
        .getAll([itemId, revision]) ??
      this.context.db.getAllFromIndex(
        "revisions",
        "by-item-revision",
        [itemId, revision],
      )
    );
    const value = values.sort((a, b) => a.entityType.localeCompare(b.entityType))[0];
    return value ? checkedRevision(value) : null;
  }

  async list(
    itemId: EntityId,
    options: RevisionListOptions = {},
  ): Promise<PageResult<RevisionRecord>> {
    const values = (await (
      this.context.transaction
        ?.objectStore("revisions")
        .index("by-item")
        .getAll(itemId) ??
      this.context.db.getAllFromIndex("revisions", "by-item", itemId)
    ))
      .map(checkedRevision)
      .filter((value) => options.entityType === undefined || value.entityType === options.entityType)
      .sort((a, b) => b.revision - a.revision || a.id.localeCompare(b.id));
    return page(values, options, (value) => value.revision);
  }
}

export class IndexedDbAttachmentRepository implements AttachmentRepository {
  public constructor(private readonly context: RepositoryContext) {}

  async get(id: EntityId): Promise<AttachmentRecord | null> {
    return cloneOrNull(await this.context.db.get("attachments", id));
  }

  async getBySha256(sha256: string): Promise<AttachmentRecord | null> {
    return cloneOrNull(
      await this.context.db.getFromIndex("attachments", "by-sha256", sha256),
    );
  }

  async createIfAbsent(
    record: AttachmentRecord,
  ): Promise<{ record: AttachmentRecord; created: boolean }> {
    assertWrite(this.context);
    return runWrite(this.context, ["attachments", "changes"], async (tx) => {
      const existing = await tx.objectStore("attachments")
        .index("by-sha256")
        .get(record.sha256);
      if (existing) return { record: clone(existing), created: false };
      await tx.objectStore("attachments").add(clone(record));
      await appendChange(tx, {
        entityId: record.id,
        entityType: "attachment",
        operation: "create",
        revision: record.revision,
        committedAt: record.createdAt,
        payload: { sha256: record.sha256, byteLength: record.byteLength },
      });
      return { record: clone(record), created: true };
    });
  }

  async updateProjection(
    id: EntityId,
    patch: { referenceCount: number; unreferencedAt?: UnixTimestamp },
  ): Promise<AttachmentRecord> {
    assertWrite(this.context);
    return runWrite(this.context, ["attachments"], async (tx) => {
      const current = await tx.objectStore("attachments").get(id);
      if (!current) throw new EntityNotFoundError("attachment", id);
      const { unreferencedAt: _old, ...required } = current;
      const value: AttachmentRecord = {
        ...required,
        referenceCount: patch.referenceCount,
        ...(patch.unreferencedAt === undefined
          ? {}
          : { unreferencedAt: patch.unreferencedAt }),
      };
      await tx.objectStore("attachments").put(value);
      return clone(value);
    });
  }

  async list(): Promise<readonly AttachmentRecord[]> {
    return (await this.context.db.getAll("attachments")).map(clone);
  }

  async deleteIfUnreferenced(
    id: EntityId,
    cutoff: UnixTimestamp,
  ): Promise<AttachmentRecord | null> {
    assertWrite(this.context);
    return runWrite(this.context, ["attachments", "changes"], async (tx) => {
      const current = await tx.objectStore("attachments").get(id);
      if (
        !current ||
        current.referenceCount !== 0 ||
        current.unreferencedAt === undefined ||
        current.unreferencedAt > cutoff
      ) {
        return null;
      }
      await tx.objectStore("attachments").delete(id);
      await appendChange(tx, {
        entityId: id,
        entityType: "attachment",
        operation: "delete",
        revision: current.revision,
        committedAt: cutoff,
        payload: { storageKey: current.storageKey },
      });
      return clone(current);
    });
  }
}

class IndexedDbChangeFeed implements ChangeFeedPort {
  public constructor(private readonly context: RepositoryContext) {}

  async list(afterSequence = 0, limit = 200): Promise<ChangeFeedPage> {
    if (!Number.isSafeInteger(limit) || limit < 1 || limit > 1_000) {
      throw new RangeError("Change feed limit must be between 1 and 1000");
    }
    const tx = this.context.db.transaction("changes");
    const store = tx.objectStore("changes");
    const results: Required<ChangeRecord>[] = [];
    let cursor = await store.openCursor(IDBKeyRange.lowerBound(afterSequence, true));
    while (cursor && results.length < limit) {
      results.push({
        ...clone(cursor.value),
        sequence: cursor.primaryKey,
      });
      cursor = await cursor.continue();
    }
    const nextCursor = results.at(-1)?.sequence;
    return {
      changes: results,
      ...(results.length === limit && nextCursor !== undefined
        ? { cursor: nextCursor }
        : {}),
    };
  }
}

async function requireFolderParent(
  tx: WriteTransaction,
  id: string,
  parentId: string | undefined,
): Promise<void> {
  if (parentId === undefined) return;
  if (parentId === id) {
    throw new ConstraintViolationError("A folder cannot be its own parent");
  }
  const parent = await tx.objectStore("folders").get(parentId);
  if (!parent || parent.deletedAt !== undefined) {
    throw new ConstraintViolationError("Folder parent is unavailable");
  }
  const seen = new Set([id]);
  let cursor: Folder | undefined = parent;
  while (cursor) {
    if (seen.has(cursor.id)) {
      throw new ConstraintViolationError("Folder hierarchy contains a cycle");
    }
    seen.add(cursor.id);
    cursor =
      cursor.parentId === undefined
        ? undefined
        : await tx.objectStore("folders").get(cursor.parentId);
  }
}

function deletionPredicate<T extends { deletedAt?: number }>(
  options: EntityListOptions,
): (value: T) => boolean {
  const deletion = options.deletion ?? "active";
  return (value) =>
    deletion === "all" ||
    (deletion === "active"
      ? value.deletedAt === undefined
      : value.deletedAt !== undefined);
}

function page<T>(
  sorted: readonly T[],
  options: { cursor?: string; limit?: number },
  sortValue: (value: T) => number | string,
): PageResult<T> {
  const limit = options.limit ?? 100;
  if (!Number.isSafeInteger(limit) || limit < 1 || limit > 500) {
    throw new RangeError("Page limit must be between 1 and 500");
  }
  let start = 0;
  if (options.cursor !== undefined) {
    const decoded = decodeCursor(options.cursor);
    start = sorted.findIndex(
      (value, index) =>
        index >= 0 &&
        `${sortValue(value)}\u0000${entityId(value)}` === decoded,
    ) + 1;
    if (start === 0) throw new InvalidCursorError(options.cursor);
  }
  const items = sorted.slice(start, start + limit).map(clone);
  const last = items.at(-1);
  return start + limit < sorted.length && last !== undefined
    ? {
        items,
        nextCursor: encodeCursor(`${sortValue(last)}\u0000${entityId(last)}`),
      }
    : { items };
}

function entityId(value: unknown): string {
  if (
    typeof value === "object" &&
    value !== null &&
    "id" in value &&
    typeof value.id === "string"
  ) {
    return value.id;
  }
  throw new TypeError("Paged entity has no id");
}

function encodeCursor(value: string): string {
  return encodeURIComponent(value);
}

function decodeCursor(value: string): string {
  try {
    const decoded = decodeURIComponent(value);
    if (!decoded.includes("\u0000")) throw new Error("malformed");
    return decoded;
  } catch {
    throw new InvalidCursorError(value);
  }
}

function purgeStoreNames(): KnowledgeStoreName[] {
  return ["items", "documents", "canvases", "revisions", "attachments"];
}

function parseTrashRetentionPolicy(value: unknown): TrashRetentionPolicy {
  if (
    typeof value !== "object" ||
    value === null ||
    !("automaticCleanup" in value) ||
    typeof value.automaticCleanup !== "boolean" ||
    !("retentionDays" in value) ||
    typeof value.retentionDays !== "number"
  ) {
    throw new IndexedDbStorageError(
      "corrupt-data",
      "The stored trash retention policy is invalid.",
    );
  }
  const policy: TrashRetentionPolicy = {
    automaticCleanup: value.automaticCleanup,
    retentionDays: value.retentionDays,
  };
  try {
    validateTrashRetentionPolicy(policy);
  } catch (cause) {
    throw new IndexedDbStorageError(
      "corrupt-data",
      "The stored trash retention policy is invalid.",
      { cause },
    );
  }
  return policy;
}

function collectAttachmentOccurrences(
  value: unknown,
): ReadonlyMap<EntityId, number> {
  const occurrences = new Map<EntityId, number>();
  const add = (attachmentId: string): void => {
    if (attachmentId.length === 0) return;
    occurrences.set(attachmentId, (occurrences.get(attachmentId) ?? 0) + 1);
  };
  const visit = (candidate: unknown): void => {
    if (typeof candidate === "string") {
      if (!candidate.startsWith("attachment:")) return;
      const encoded = candidate.slice("attachment:".length).replace(/^\/\//, "");
      try {
        add(decodeURIComponent(encoded));
      } catch {
        // Invalid editor references are ignored; content validation owns repair.
      }
      return;
    }
    if (Array.isArray(candidate)) {
      for (const entry of candidate) visit(entry);
      return;
    }
    if (typeof candidate !== "object" || candidate === null) return;
    for (const [key, nested] of Object.entries(candidate)) {
      if (key === "attachmentId" && typeof nested === "string") {
        add(nested);
      } else {
        visit(nested);
      }
    }
  };
  visit(value);
  return occurrences;
}

async function reconcileAttachmentReferences(
  tx: WriteTransaction,
  previous: DomainDocument | DomainCanvas | undefined,
  next: DomainDocument | DomainCanvas,
  committedAt: UnixTimestamp,
): Promise<void> {
  const before = collectAttachmentOccurrences(previous);
  const after = collectAttachmentOccurrences(next);
  const attachmentIds = new Set([...before.keys(), ...after.keys()]);
  for (const attachmentId of attachmentIds) {
    const delta =
      (after.get(attachmentId) ?? 0) - (before.get(attachmentId) ?? 0);
    if (delta === 0) continue;
    const current = await tx.objectStore("attachments").get(attachmentId);
    if (current === undefined) {
      if (delta > 0) {
        throw new ConstraintViolationError(
          `Content references missing attachment ${attachmentId}`,
        );
      }
      continue;
    }
    const referenceCount = Math.max(0, current.referenceCount + delta);
    const { unreferencedAt: _previous, ...required } = current;
    await tx.objectStore("attachments").put({
      ...required,
      referenceCount,
      ...(referenceCount === 0 ? { unreferencedAt: committedAt } : {}),
    });
  }
}

async function runWrite<T>(
  context: RepositoryContext,
  stores: KnowledgeStoreName[],
  work: (transaction: WriteTransaction) => Promise<T>,
): Promise<T> {
  assertWrite(context);
  if (context.transaction) return work(context.transaction);
  const transaction = context.db.transaction(stores, "readwrite", {
    durability: "strict",
  });
  try {
    const result = await work(transaction);
    await transaction.done;
    return result;
  } catch (error) {
    try {
      transaction.abort();
    } catch {
      // The browser may already have aborted the transaction.
    }
    try {
      await transaction.done;
    } catch {
      // Preserve the useful error.
    }
    throw mapWriteError(error);
  }
}

async function appendChange(
  tx: WriteTransaction,
  change: ChangeRecord,
  retention = DEFAULT_HISTORY_RETENTION_POLICY.changesPerEntity,
): Promise<void> {
  await tx.objectStore("changes").add(clone(change));
  const keys = await tx.objectStore("changes").index("by-entity").getAllKeys(change.entityId);
  const overflow = keys.length - retention;
  for (const key of keys.slice(0, Math.max(0, overflow))) {
    await tx.objectStore("changes").delete(key);
  }
}

async function pruneRevisions(
  tx: WriteTransaction,
  itemId: string,
  retention: number,
): Promise<void> {
  const revisions = await tx.objectStore("revisions").index("by-item").getAll(itemId);
  revisions.sort((a, b) => b.revision - a.revision || a.id.localeCompare(b.id));
  for (const revision of revisions.slice(retention)) {
    await tx.objectStore("revisions").delete(revision.id);
  }
}

function changeFor(
  value: Folder | Tag,
  entityType: "folder" | "tag",
  operation: ChangeRecord["operation"],
  payload: unknown,
): ChangeRecord {
  return {
    entityId: value.id,
    entityType,
    operation,
    revision: value.revision,
    committedAt: value.updatedAt,
    payload,
  };
}

function assertRevision(
  id: string,
  expected: number,
  actual: number,
): void {
  if (actual !== expected) {
    throw new OptimisticConcurrencyError(id, expected, actual);
  }
}

function assertWrite(context: RepositoryContext): void {
  if (context.readOnly) {
    throw new ReadOnlyStorageError(
      "This knowledge base is newer than this FengYue Notes build and is read-only.",
    );
  }
}

function checkedRevision(value: RevisionRecord): RevisionRecord {
  try {
    const snapshot = value.entityType === "document"
      ? migrateDocumentToCurrent(
          clone(value.snapshot) as unknown as DomainDocument,
        ).value
      : migrateCanvasToCurrent(
          clone(value.snapshot) as unknown as DomainCanvas,
        ).value;
    const normalized: RevisionRecord = {
      ...value,
      schemaVersion: CURRENT_CONTENT_SCHEMA_VERSION,
      snapshot: snapshot as unknown as RevisionRecord["snapshot"],
      // IndexedDB v2/v3 snapshots predate the explicit operation field.
      operation: value.operation ?? "save",
    };
    assertRevisionRecord(normalized);
    return clone(normalized);
  } catch (error) {
    throw corrupt("revision", entityId(value), error);
  }
}

function checkedItem(value: KnowledgeItem): KnowledgeItem {
  try {
    return clone(migrateKnowledgeItemToCurrent(clone(value)).value);
  } catch (error) {
    throw corrupt("item", entityId(value), error);
  }
}

function checkedCatalogEntity<T extends Folder | Tag>(
  entityType: "folder" | "tag",
  value: T,
  assertion: (value: T) => void,
): T {
  try {
    const compatibility = classifyContentSchemaVersion(value.schemaVersion);
    const normalized = compatibility === "migratable"
      ? { ...value, schemaVersion: CURRENT_CONTENT_SCHEMA_VERSION }
      : value;
    assertion(normalized as T);
    return clone(normalized as T);
  } catch (error) {
    throw corrupt(entityType, entityId(value), error);
  }
}

function checked<T>(
  entityType: string,
  value: T,
  assertion: (value: T) => void,
): T {
  try {
    assertion(value);
    return clone(value);
  } catch (error) {
    throw corrupt(entityType, entityId(value), error);
  }
}

function corrupt(
  entityType: string,
  id: string,
  cause: unknown,
): IndexedDbStorageError {
  return new IndexedDbStorageError(
    "corrupt-data",
    `Stored ${entityType} ${id} is corrupt. Export recovery data before resetting.`,
    { cause },
  );
}

function mapWriteError(error: unknown): unknown {
  if (
    error instanceof EntityAlreadyExistsError ||
    error instanceof EntityNotFoundError ||
    error instanceof OptimisticConcurrencyError ||
    error instanceof ConstraintViolationError ||
    error instanceof ReadOnlyStorageError ||
    error instanceof IndexedDbStorageError
  ) {
    return error;
  }
  return mapIndexedDbError(error, "The browser could not persist the change.");
}

function isVersionError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    error.name === "VersionError"
  );
}

function clone<T>(value: T): T {
  return structuredClone(value);
}

function cloneOrNull<T>(value: T | undefined): T | null {
  return value === undefined ? null : clone(value);
}

/** Small helpers used by migration/import tests and first-run samples. */
export const indexedDbFixtures = {
  createFolder,
  createTag,
  contentSchemaVersion: CURRENT_CONTENT_SCHEMA_VERSION,
};
